import React, { useState, useEffect } from "react";
import { base_url } from "../config/config";
import * as XLSX from "xlsx";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * Helper function to find an ANR number in a string.
 * It looks for optional '#' or 'A' (with optional space), then "XXXX-XX-XX-XX".
 */
function extractAnrText(text) {
  if (!text) return null;
  const re = /(?:#|A\s*)?(\d{4}-\d{2}-\d{2}-\d{2})/;
  const match = text.match(re);
  return match ? match[1] : null;
}

/**
 * Returns the first matched ANR without trailing slash, or "N/A".
 * Returns the first matched ANR without trailing slash, or "N/A".
 */
function getAnrNumber(invoice) {
  if (!invoice) return "N/A";

  // Check line items first
  // Check line items first
  if (Array.isArray(invoice.lineItems)) {
    for (const item of invoice.lineItems) {
      const maybeAnr = extractAnrText(item?.description);
      if (maybeAnr) return maybeAnr;
      if (maybeAnr) return maybeAnr;
    }
  }

  // Introduction
  const anrFromIntro = extractAnrText(invoice.introduction);
  if (anrFromIntro) return anrFromIntro;
  if (anrFromIntro) return anrFromIntro;

  // Remark
  const anrFromRemark = extractAnrText(invoice.remark);
  if (anrFromRemark) return anrFromRemark;
  if (anrFromRemark) return anrFromRemark;

  // Customer reference
  const anrFromRef = extractAnrText(invoice.customerReferenceNumber);
  if (anrFromRef) return anrFromRef;
  if (anrFromRef) return anrFromRef;

  // Anywhere in JSON (as last resort)
  // Anywhere in JSON (as last resort)
  const invoiceString = JSON.stringify(invoice);
  const anrFromAnywhere = extractAnrText(invoiceString);
  if (anrFromAnywhere) return anrFromAnywhere;
  if (anrFromAnywhere) return anrFromAnywhere;

  return "N/A";
}

/**
 * Shows a detailed view of the selected invoice, including a line items table.
 */
const InvoiceDetail = ({ invoice, onClose }) => {
  if (!invoice) return null;

  return (
    <div className="p-4 bg-white rounded shadow border border-gray-200 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">
          Invoice Details: {invoice.voucherNumber}
        </h2>
        <button
          onClick={onClose}
          className="px-2 py-1 bg-red-500 text-white rounded"
        >
          Close
        </button>
      </div>

      {/* Basic Invoice Info */}
      <div className="mb-4">
        <p>
          <strong>Invoice ID:</strong> {invoice.id}
        </p>
        <p>
          <strong>ANR Number (line items or introduction):</strong>{" "}
          {getAnrNumber(invoice)}
        </p>
        <p>
          <strong>Introduction:</strong> {invoice.introduction}
        </p>
      </div>

      {/* Line Items Table */}
      <div className="mb-4">
        <h3 className="font-semibold mb-2">Line Items</h3>
        {invoice.lineItems && invoice.lineItems.length > 0 ? (
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-left">Quantity</th>
                <th className="px-3 py-2 text-left">Unit</th>
                <th className="px-3 py-2 text-left">Unit Price (Net)</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lineItems.map((item, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-3 py-2">{item.name || "N/A"}</td>
                  <td className="px-3 py-2">{item.description || "N/A"}</td>
                  <td className="px-3 py-2">{item.quantity || "N/A"}</td>
                  <td className="px-3 py-2">{item.unitName || "N/A"}</td>
                  <td className="px-3 py-2">
                    {item.unitPrice
                      ? `${item.unitPrice.netAmount} ${item.unitPrice.currency}`
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No line items found.</p>
        )}
      </div>
    </div>
  );
};

/**
 * Contact Person Popup that shows details for multiple contacts
 */
const ContactPersonPopup = ({ contacts, onClose }) => {
  if (!contacts || contacts.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Contact Persons</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="divide-y">
          {contacts.map((person, idx) => (
            <div key={idx} className="py-3">
              <div className="flex items-center mb-1">
                <h3 className="font-medium">
                  {[person.salutation, person.firstName, person.lastName]
                    .filter(Boolean)
                    .join(" ")}
                  {person.primary && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      Primary
                    </span>
                  )}
                </h3>
              </div>
              {person.emailAddress && (
                <p className="text-sm flex items-center text-gray-600 mb-1">
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  {person.emailAddress}
                </p>
              )}
              {person.phoneNumber && (
                <p className="text-sm flex items-center text-gray-600">
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  {person.phoneNumber}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Pagination component for navigating through pages
 */
const PaginationControls = ({
  pagination,
  onPageChange,
  onPageSizeChange,
  isLoading,
}) => {
  const { page, totalPages, first, last, totalElements, size } = pagination;

  console.log("Pagination state:", {
    page,
    totalPages,
    first,
    last,
    totalElements,
    size,
  });

  const startItem = page * size + 1;
  const endItem = Math.min((page + 1) * size, totalElements);

  const pageSizeOptions = [5, 10, 20, 50, 100];

  return (
    <div className="flex items-center justify-between mt-4 p-4 bg-gray-50 rounded">
      <div className="flex items-center space-x-4">
        <div className="text-sm text-gray-600">
          Showing {startItem} to {endItem} of {totalElements} invoices
        </div>

        {/* <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Per page:</label>
          <select
            value={size}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={isLoading}
            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div> */}
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={() => onPageChange(0)}
          disabled={first || isLoading}
          className={`px-3 py-1 rounded text-sm ${
            first || isLoading
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          First
        </button>

        <button
          onClick={() => onPageChange(page - 1)}
          disabled={first || isLoading}
          className={`px-3 py-1 rounded text-sm ${
            first || isLoading
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          Previous
        </button>

        <span className="px-3 py-1 text-sm bg-white border rounded">
          Page {page + 1} of {totalPages}
        </span>

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={last || isLoading}
          className={`px-3 py-1 rounded text-sm ${
            last || isLoading
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          Next
        </button>

        <button
          onClick={() => onPageChange(totalPages - 1)}
          disabled={last || isLoading}
          className={`px-3 py-1 rounded text-sm ${
            last || isLoading
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          Last
        </button>
      </div>
    </div>
  );
};

/**
 * Main Lexware component
 */
const Lexware = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [dataType, setDataType] = useState(null);
  const [data, setData] = useState([]);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });
  const [retryTime, setRetryTime] = useState(null);
  const [retryTimeoutId, setRetryTimeoutId] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [contactRole, setContactRole] = useState("both");
  const [selectedContactPersons, setSelectedContactPersons] = useState(null);
  const [importStatus, setImportStatus] = useState({
    success: false,
    message: "",
    error: false,
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    size: 100,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false,
  });

  // Excel import state
  const [workData, setWorkData] = useState([]);
  const [invoiceNo, setInvoiceNo] = useState("");

  const API_BASE_URL = base_url + "/lex";
  // CNC backend API URL
  const CNC_API_BASE_URL = base_url;

  // IMPORT
  const IMPORT_URL = base_url + "/import/importInvoices";

  // Handle page change for pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages && !isLoading) {
      fetchData(dataType, newPage);
    }
  };
  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPagination((prev) => ({ ...prev, size: newSize, page: 0 }));
    console.log("Page size changed to:", newSize, dataType, pagination);
    if (dataType === "invoices") {
      // Use a timeout to ensure state is updated before making the API call
      setTimeout(() => {
        fetchData(dataType, 0);
      }, 300);
    }
  };

  // Handle Excel file upload
  const saveProcessedData = async () => {
    try {
      if (!workData.length) {
        toast.warn("Keine Daten zum Speichern vorhanden.");
        return;
      }

      if (!invoiceNo.trim()) {
        toast.warn("Bitte geben Sie eine Rechnungsnummer ein.");
        return;
      }

      console.log("workData", workData);
      const response = await axios.post(`${base_url}/v1/saveExcelData`, {
        invoiceNo,
        mergedData: workData,
      });

      if (response.data.status === 1) {
        console.log("Data successfully saved to the database.");
        toast.success("Daten erfolgreich gespeichert!");
        fetchData();
      } else {
        console.error("Failed to save data:", response.data.message);
        toast.error(`Fehler beim Speichern: ${response.data.message}`);
      }
    } catch (error) {
      console.error("Error saving processed data:", error);
      toast.error(
        "Fehler beim Speichern der Daten. Bitte versuchen Sie es später erneut."
      );
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];

    if (!file) {
      toast.warn("Keine Datei ausgewählt.");
      return;
    }

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(file.type)) {
      toast.error(
        "Bitte wählen Sie eine gültige Excel-Datei (.xlsx oder .xls)."
      );
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const binaryStr = e.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        const extractedData = jsonData
          .filter(
            (row) => row.Bezeichnung && row.Einzelpreis && row.Gesamtpreis
          )
          .map((row) => {
            // Extract only the first line (before any newline or line break)
            const firstLine = row.Bezeichnung.split(/[\r\n]+/)[0];
            let cleanedBezeichnung = firstLine.replace(/[^\d-]/g, "");

            if (cleanedBezeichnung.includes("-")) {
              const parts = cleanedBezeichnung.split("-");
              const lastPart = parts.pop();
              const truncatedLastPart = lastPart.slice(0, 2);
              cleanedBezeichnung = [...parts, truncatedLastPart].join("-");
            }

            return {
              ANR: row.ANR,
              Bezeichnung: cleanedBezeichnung,
              Einzelpreis: parseFloat(row.Einzelpreis),
              Gesamtpreis: parseFloat(row.Gesamtpreis),
              AdditionalText: row.AdditionalText || "",
              invoiceNo: invoiceNo,
            };
          });

        if (extractedData.length === 0) {
          toast.warn(
            "Keine gültigen Daten in der Excel-Datei gefunden. Stellen Sie sicher, dass die Spalten 'Bezeichnung', 'Einzelpreis' und 'Gesamtpreis' vorhanden sind."
          );
          return;
        }

        console.log(extractedData);
        setWorkData(extractedData);
        toast.success(
          `Excel-Datei erfolgreich importiert! ${extractedData.length} Einträge geladen.`
        );
      } catch (error) {
        console.error("Error processing file:", error);
        toast.error(
          "Fehler beim Verarbeiten der Excel-Datei. Bitte überprüfen Sie das Dateiformat."
        );
      }
    };

    reader.onerror = () => {
      toast.error("Fehler beim Lesen der Datei.");
    };

    reader.readAsBinaryString(file);
  };

  useEffect(() => {
    checkConnection();
    return () => {
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, []);

  const checkConnection = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/status`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Status check failed with code ${response.status}`
        );
      }
      const result = await response.json();
      setIsConnected(result.status === "connected");
    } catch (err) {
      console.error("API connection error:", err);
      setError(`Cannot connect to API: ${err.message}`);
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchData = async (type, page = 0) => {
    if (retryTimeoutId) {
      clearTimeout(retryTimeoutId);
      setRetryTimeoutId(null);
    }

    setIsLoading(true);
    setError("");
    setDataType(type);
    setInvoiceDetail(null); // Reset detail view

    try {
      let endpoint = "";
      let queryParams = "";

      switch (type) {
        case "invoices":
          endpoint = "/invoices";
          queryParams = `?page=${page}&size=${pagination.size}`;
          break;
        case "contacts":
          endpoint = "/contacts";
          queryParams = "?page=0&size=100&direction=both";
          break;
        case "profile":
          endpoint = "/profile";
          break;
        case "voucherlist":
          endpoint = "/voucherlist";
          queryParams = `?voucherType=invoice&voucherStatus=open,paid,paidoff,voided,transferred&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&page=0&size=100`;
          break;
        default:
          throw new Error("Unknown data type");
      }

      const url = `${API_BASE_URL}${endpoint}${queryParams}`;
      console.log("Fetching data from:", url);

      if (testMode && type === "invoices") {
        // Generate test data with a special ANR number
        const testData = generateTestData();
        setData(testData);
      } else {
        const response = await fetch(url);
        if (!response.ok) {
          const errorData = await response.json();
          if (response.status === 429) {
            const retryAfter = errorData.retryAfter || 60;
            setRetryTime(retryAfter);
            const timeoutId = setTimeout(() => {
              setRetryTime(null);
              fetchData(type, page);
            }, retryAfter * 1000);
            setRetryTimeoutId(timeoutId);
            throw new Error(
              `Rate limit exceeded. Will retry in ${retryAfter} seconds.`
            );
          }
          throw new Error(
            errorData.error || `Request failed with status ${response.status}`
          );
        }
        const responseData = await response.json();

        let processedData;
        if (type === "profile") {
          processedData = [responseData];
        } else if (responseData.content) {
          processedData = responseData.content;
          if (type === "invoices") {
            setPagination({
              page: responseData.number || 0,
              size: responseData.size,
              totalElements: responseData.totalElements || 0,
              totalPages: responseData.totalPages || 0,
              first: responseData.first || false,
              last: responseData.last || false,
            });
          }
        } else {
          processedData = Array.isArray(responseData)
            ? responseData
            : [responseData];
        }

        setData(processedData);
        console.log(`Fetched ${processedData.length} items for ${type}`);
      }
    } catch (err) {
      console.error("Data fetch error:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // If "testMode", generate dummy invoice data
  const generateTestData = () => {
    return [
      {
        id: "test-invoice-1",
        voucherNumber: "RE-0001",
        voucherDate: "2023-01-01",
        contactName: "Test Customer",
        introduction: "Test introduction",
        remark: "Test remark with 0094-00-00-25",
        lineItems: [
          {
            name: "Test Product",
            description: "Product description",
            quantity: 1,
            unitName: "Piece",
            unitPrice: { netAmount: 100, currency: "EUR" },
          },
        ],
      },
      {
        id: "test-invoice-2",
        voucherNumber: "RE-0002",
        voucherDate: "2023-01-02",
        contactName: "Another Customer",
        introduction: "Introduction with ANR #0094-00-00-25",
        lineItems: [
          {
            name: "Another Product",
            description: "Product with A 0094-00-00-25",
            quantity: 2,
            unitName: "Hour",
            unitPrice: { netAmount: 75, currency: "EUR" },
          },
        ],
      },
    ];
  };

  const fetchInvoiceDetails = async (invoiceId) => {
    setIsLoading(true);
    setError("");
    try {
      if (testMode) {
        const invoice = data.find((inv) => inv.id === invoiceId);
        if (invoice) {
          setInvoiceDetail(invoice);
        } else {
          throw new Error("Invoice not found in test data");
        }
      } else {
        const url = `${API_BASE_URL}/invoices/${invoiceId}`;
        console.log("Fetching invoice details from:", url);

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch invoice details: ${response.status}`
          );
        }
        const invoiceData = await response.json();
        setInvoiceDetail(invoiceData);
      }
    } catch (err) {
      console.error("Error fetching invoice details:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateRangeChange = (e, field) => {
    const newDateRange = { ...dateRange, [field]: e.target.value };
    setDateRange(newDateRange);
  };

  const exportData = () => {
    if (!data || data.length === 0) return;
    const fileName = `lexoffice-${dataType}-${
      new Date().toISOString().split("T")[0]
    }.json`;
    const jsonStr = JSON.stringify(data, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(
      jsonStr
    )}`;
    const link = document.createElement("a");
    link.setAttribute("href", dataUri);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const importData = async () => {
    // Show toast that import has started
    toast.info("Started importing data...", { autoClose: 2000 });

    try {
      const response = await axios.get(IMPORT_URL);
      if (response.status == 200) {
        toast.success("Import abgeschlossen!", { autoClose: 3000 });
      } else {
        toast.error("Fehler beim Importieren der Daten!", { autoClose: 3000 });
      }
    } catch (error) {
      toast.error("Fehler beim Importieren der Daten!", { autoClose: 3000 });
      console.error(error);
    }
  };
  // Function to import contacts to the CNC backend
  const importToBackend = async (type) => {
    setIsLoading(true);
    setImportStatus({ success: false, message: "", error: false });

    try {
      // First, ensure contacts data is loaded
      if (dataType !== "contacts") {
        await fetchData("contacts");
      }

      // Filter contacts based on the type (customer/vendor)
      const filteredContacts = data.filter((contact) => {
        if (type === "customer") return contact.roles?.customer;
        if (type === "vendor") return contact.roles?.vendor;
        return false;
      });

      if (filteredContacts.length === 0) {
        setImportStatus({
          success: false,
          message: `No ${
            type === "customer" ? "Kunden" : "Lieferanten"
          } found to import.`,
          error: true,
        });
        setIsLoading(false);
        return;
      }

      // Transform the data format to match the backend models
      const transformedContacts = filteredContacts.map((contact) => {
        // Get billing addresses
        const billingAddresses = contact.addresses?.billing || [];
        const shippingAddresses = contact.addresses?.shipping || [];
        const allAddresses = [...billingAddresses, ...shippingAddresses];

        // Get first address (primary)
        const primaryAddress = allAddresses.length > 0 ? allAddresses[0] : {};

        // Collect all email addresses
        const emailAddresses = [];
        if (contact.emailAddresses) {
          Object.values(contact.emailAddresses).forEach((categoryEmails) => {
            emailAddresses.push(...categoryEmails);
          });
        }

        // Collect all phone numbers
        const phoneNumbers = [];
        if (contact.phoneNumbers) {
          Object.values(contact.phoneNumbers).forEach((categoryPhones) => {
            phoneNumbers.push(...categoryPhones);
          });
        }

        // Get contact persons
        const contactPersons = contact.company?.contactPersons || [];

        return {
          konto:
            type === "customer"
              ? contact.roles?.customer?.number || contact.id
              : contact.roles?.vendor?.number || contact.id,
          bezeichnung: contact.company?.name || "",
          kurzbezeichnung: contact.company?.name || "",
          ustIdNummer: contact.company?.vatRegistrationId || "",
          address: {
            strasse: primaryAddress.street || "",
            plz: primaryAddress.zip || "",
            ort: primaryAddress.city || "",
            address: primaryAddress.supplement || "",
          },
          email: emailAddresses,
          phoneNumber: phoneNumbers,
          contactPersons: contactPersons.map((person) => ({
            name: [person.salutation, person.firstName, person.lastName]
              .filter(Boolean)
              .join(" "),
            email: person.emailAddress || "",
            phoneNumber: person.phoneNumber || "",
            anrede: person.salutation || "",
          })),
        };
      });

      // Send to the appropriate backend endpoint
      const endpoint =
        type === "customer"
          ? `${CNC_API_BASE_URL}/v1/addKunden`
          : `${CNC_API_BASE_URL}/v1/addLieferant`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(transformedContacts),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to import ${
            type === "customer" ? "Kunden" : "Lieferanten"
          }: ${response.statusText}`
        );
      }

      const result = await response.json();

      setImportStatus({
        success: true,
        message: `Successfully imported ${result.length || "all"} ${
          type === "customer" ? "Kunden" : "Lieferanten"
        }.`,
        error: false,
      });
    } catch (err) {
      console.error("Import error:", err);
      setImportStatus({
        success: false,
        message: err.message || "An error occurred during import.",
        error: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Renders the main data table based on dataType
   */
  const renderDataTable = () => {
    if (!data || data.length === 0) {
      return <p className="text-gray-500 italic">No data to display</p>;
    }

    switch (dataType) {
      /*-----------------------------------------
       * INVOICES
       ------------------------------------------*/
      case "invoices":
        return (
          <>
            {/* Excel Import Section */}

            <div>
              <p className="mb-2 text-gray-500 text-sm">
                Showing {data.length} invoices (Page {pagination.page + 1} of{" "}
                {pagination.totalPages})
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">ANR Number</th>
                      <th className="px-3 py-2 text-left">Invoice Number</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Customer</th>
                      <th className="px-3 py-2 text-left">Reference</th>
                      <th className="px-3 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.map((invoice, index) => (
                      <tr key={invoice.id || `invoice-${index}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          {getAnrNumber(invoice)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          {invoice.voucherNumber || "N/A"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          {invoice.voucherDate || "N/A"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          {invoice.contactName ||
                            invoice.address?.name ||
                            "N/A"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          {invoice.customerReferenceNumber ||
                            invoice.remark ||
                            "N/A"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <button
                            onClick={() => fetchInvoiceDetails(invoice.id)}
                            className="text-blue-600 hover:text-blue-800 mr-2"
                          >
                            View Details
                          </button>
                          <button
                            onClick={() =>
                              window.open(
                                `https://app.lexoffice.de/permalink/public/invoice/${invoice.id}`,
                                "_blank"
                              )
                            }
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Open in Lexoffice
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination Controls */}
              <PaginationControls
                pagination={pagination}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                isLoading={isLoading}
              />
            </div>
          </>
        );

      /*-----------------------------------------
       * CONTACTS
       ------------------------------------------*/
      case "contacts": {
        // Filter contacts based on selected role
        const filteredContacts = data.filter((contact) => {
          if (contactRole === "both") return true;
          if (contactRole === "customer") return contact.roles?.customer;
          if (contactRole === "vendor") return contact.roles?.vendor;
          return false;
        });

        return (
          <div>
            {/* Buttons to toggle between Kunden/Lieferanten/Alle */}
            <div className="mb-4 space-x-2">
              <button
                onClick={() => setContactRole("customer")}
                className={`px-4 py-2 rounded ${
                  contactRole === "customer"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200"
                }`}
              >
                Kunden
              </button>
              <button
                onClick={() => setContactRole("vendor")}
                className={`px-4 py-2 rounded ${
                  contactRole === "vendor"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200"
                }`}
              >
                Lieferanten
              </button>
              <button
                onClick={() => setContactRole("both")}
                className={`px-4 py-2 rounded ${
                  contactRole === "both"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200"
                }`}
              >
                Alle
              </button>

              {/* Import buttons for Kunden and Lieferant */}
              <button
                onClick={() => importToBackend("customer")}
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                disabled={isLoading}
              >
                Kunden importieren
              </button>
              <button
                onClick={() => importToBackend("vendor")}
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                disabled={isLoading}
              >
                Lieferanten importieren
              </button>
            </div>

            {/* Import status notification */}
            {importStatus.message && (
              <div
                className={`p-3 mb-4 rounded ${
                  importStatus.error
                    ? "bg-red-100 text-red-700"
                    : importStatus.success
                    ? "bg-green-100 text-green-700"
                    : "bg-blue-100 text-blue-700"
                }`}
              >
                {importStatus.message}
              </div>
            )}

            <p className="mb-2 text-gray-500 text-sm">
              Showing {filteredContacts.length} contacts (
              {contactRole === "customer"
                ? "Kunden"
                : contactRole === "vendor"
                ? "Lieferanten"
                : "Alle"}
              )
            </p>

            {/* Contact popup for multiple contact persons */}
            {selectedContactPersons && (
              <ContactPersonPopup
                contacts={selectedContactPersons}
                onClose={() => setSelectedContactPersons(null)}
              />
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Role & Number</th>
                    <th className="px-3 py-2 text-left">Company Name</th>
                    <th className="px-3 py-2 text-left">Tax#/VAT</th>
                    <th className="px-3 py-2 text-left">Contact Persons</th>
                    <th className="px-3 py-2 text-left">Billing Address(es)</th>
                    <th className="px-3 py-2 text-left">
                      Shipping Address(es)
                    </th>
                    <th className="px-3 py-2 text-left">All Email(s)</th>
                    <th className="px-3 py-2 text-left">All Phone(s)</th>
                    <th className="px-3 py-2 text-left">XRechnung</th>
                    <th className="px-3 py-2 text-left">Note</th>
                    <th className="px-3 py-2 text-left">Archived?</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map((contact, idx) => {
                    // Improved display of customer/vendor roles
                    const hasCustomerRole = !!contact.roles?.customer;
                    const hasVendorRole = !!contact.roles?.vendor;
                    const customerNo = contact.roles?.customer?.number;
                    const vendorNo = contact.roles?.vendor?.number;

                    // Contact persons can be multiple
                    const contactPersons =
                      contact.company?.contactPersons || [];

                    // Primary contact person (for display in table)
                    const primaryContact =
                      contactPersons.find((p) => p.primary) ||
                      contactPersons[0];
                    const contactDisplayName = primaryContact
                      ? [
                          primaryContact.salutation,
                          primaryContact.firstName,
                          primaryContact.lastName,
                        ]
                          .filter(Boolean)
                          .join(" ")
                      : "—";

                    // Addresses - billing
                    let billingAddrText = "—";
                    if (contact.addresses?.billing?.length) {
                      billingAddrText = contact.addresses.billing
                        .map((b) => {
                          // e.g.: "Rechnungsadressenzusatz, Hauptstr. 5, 12345 Musterort, DE"
                          return [
                            b.supplement,
                            b.street,
                            b.zip && b.city ? `${b.zip} ${b.city}` : "",
                            b.countryCode,
                          ]
                            .filter(Boolean)
                            .join(", ");
                        })
                        .join("\n");
                    }

                    // Addresses - shipping
                    let shippingAddrText = "—";
                    if (contact.addresses?.shipping?.length) {
                      shippingAddrText = contact.addresses.shipping
                        .map((s) => {
                          return [
                            s.supplement,
                            s.street,
                            s.zip && s.city ? `${s.zip} ${s.city}` : "",
                            s.countryCode,
                          ]
                            .filter(Boolean)
                            .join(", ");
                        })
                        .join("\n");
                    }

                    // Emails => flatten all categories
                    let allEmails = [];
                    if (contact.emailAddresses) {
                      const categories = Object.values(contact.emailAddresses); // e.g. [ ["business@..."], ["office@..."], ... ]
                      allEmails = allEmails.concat(...categories);
                    }
                    const emailText = allEmails.length
                      ? allEmails.join("\n")
                      : "—";

                    // Phones => flatten
                    let allPhones = [];
                    if (contact.phoneNumbers) {
                      const phoneCats = Object.values(contact.phoneNumbers);
                      allPhones = allPhones.concat(...phoneCats);
                    }
                    const phoneText = allPhones.length
                      ? allPhones.join("\n")
                      : "—";

                    // XRechnung
                    let xRechnungText = "—";
                    if (contact.xRechnung) {
                      const { buyerReference, vendorNumberAtCustomer } =
                        contact.xRechnung;
                      xRechnungText = [
                        `buyerReference: ${buyerReference}`,
                        `vendorNumberAtCustomer: ${vendorNumberAtCustomer}`,
                      ].join("\n");
                    }

                    // Note, Archived
                    const note = contact.note || "—";
                    const archived = contact.archived ? "Yes" : "No";

                    return (
                      <tr
                        key={contact.id || idx}
                        className="border-t align-top"
                      >
                        {/* Improved customer/vendor display */}
                        <td className="px-3 py-2 text-sm">
                          <div className="flex flex-col space-y-1">
                            {hasCustomerRole && (
                              <div className="flex items-center">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                                  Customer
                                </span>
                                {customerNo ? (
                                  <span className="font-medium">
                                    {customerNo}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 italic">
                                    No ID
                                  </span>
                                )}
                              </div>
                            )}
                            {hasVendorRole && (
                              <div className="flex items-center">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                                  Vendor
                                </span>
                                {vendorNo ? (
                                  <span className="font-medium">
                                    {vendorNo}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 italic">
                                    No ID
                                  </span>
                                )}
                              </div>
                            )}
                            {!hasCustomerRole && !hasVendorRole && (
                              <span className="text-gray-400 italic">
                                No roles assigned
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          {contact.company?.name || "—"}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <div className="space-y-1">
                            {contact.company?.taxNumber && (
                              <div>
                                <span className="font-medium text-xs">
                                  Tax#:
                                </span>{" "}
                                {contact.company.taxNumber}
                              </div>
                            )}
                            {contact.company?.vatRegistrationId && (
                              <div>
                                <span className="font-medium text-xs">
                                  VAT ID:
                                </span>{" "}
                                {contact.company.vatRegistrationId}
                              </div>
                            )}
                            {!contact.company?.taxNumber &&
                              !contact.company?.vatRegistrationId &&
                              "—"}
                          </div>
                        </td>
                        {/* Contact Persons with popup trigger */}
                        <td className="px-3 py-2 text-sm">
                          {contactPersons.length > 0 ? (
                            <div>
                              <div className="flex items-center">
                                <span>{contactDisplayName}</span>
                                {contactPersons.length > 1 && (
                                  <button
                                    onClick={() =>
                                      setSelectedContactPersons(contactPersons)
                                    }
                                    className="ml-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs px-2 py-1 rounded"
                                  >
                                    +{contactPersons.length - 1} more
                                  </button>
                                )}
                              </div>
                              {primaryContact?.emailAddress && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {primaryContact.emailAddress}
                                </div>
                              )}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm whitespace-pre-wrap">
                          {billingAddrText}
                        </td>
                        <td className="px-3 py-2 text-sm whitespace-pre-wrap">
                          {shippingAddrText}
                        </td>
                        <td className="px-3 py-2 text-sm whitespace-pre-wrap">
                          {emailText}
                        </td>
                        <td className="px-3 py-2 text-sm whitespace-pre-wrap">
                          {phoneText}
                        </td>
                        <td className="px-3 py-2 text-sm whitespace-pre-wrap">
                          {emailText}
                        </td>
                        <td className="px-3 py-2 text-sm whitespace-pre-wrap">
                          {phoneText}
                        </td>
                        <td className="px-3 py-2 text-sm whitespace-pre-wrap">
                          {xRechnungText}
                        </td>
                        <td className="px-3 py-2 text-sm whitespace-pre-wrap">
                          {note}
                        </td>
                        <td className="px-3 py-2 text-sm">{archived}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      /*-----------------------------------------
       * PROFILE
       ------------------------------------------*/
      case "profile":
        return (
          <div className="bg-white p-4 rounded-md shadow">
            <h3 className="text-xl font-medium mb-4">Company Profile</h3>
            {data[0] && (
              <div className="space-y-2">
                <p>
                  <strong>Name:</strong> {data[0].name || "N/A"}
                </p>
                <p>
                  <strong>Tax Number:</strong> {data[0].taxNumber || "N/A"}
                </p>
                <p>
                  <strong>VAT ID:</strong> {data[0].vatRegistrationId || "N/A"}
                </p>
                {data[0].address && (
                  <div>
                    <strong>Address:</strong>
                    <p className="ml-4">
                      {data[0].address.street || ""}{" "}
                      {data[0].address.streetNumber || ""} <br />
                      {data[0].address.zip || ""} {data[0].address.city || ""}{" "}
                      <br />
                      {data[0].address.countryCode || ""}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      /*-----------------------------------------
       * VOUCHERLIST
       ------------------------------------------*/
      case "voucherlist":
        return (
          <div>
            <p className="mb-2 text-gray-500 text-sm">
              Showing {data.length} vouchers
            </p>
            {/* Render your voucher list if desired */}
          </div>
        );

      default:
        return <p>Unknown data type</p>;
    }
  };

  const renderRetryCountdown = () => {
    if (!retryTime) return null;
    return (
      <div className="mt-2 text-sm text-blue-600">
        Retrying in {retryTime} seconds...
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Lexoffice Data Access
        </h1>
        <div className="flex items-center">
          {isConnected ? (
            <div className="flex items-center space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={testMode}
                  onChange={() => setTestMode(!testMode)}
                  className="form-checkbox h-4 w-4 text-blue-600"
                />
                <span className="ml-2 text-sm text-gray-700">Test Mode</span>
              </label>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                Connected
              </span>
            </div>
          ) : (
            <button
              onClick={checkConnection}
              disabled={isLoading}
              className={`py-2 px-4 rounded-md text-white font-medium ${
                isLoading ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              {isLoading ? "Connecting..." : "Check Connection"}
            </button>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
          {renderRetryCountdown()}
        </div>
      )}

      {/* If connected, show data controls */}
      {isConnected && (
        <>
          <div className="mb-6">
            <p className="mb-4 text-gray-700">
              Select the type of data you'd like to view. Each option loads data
              on-demand.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => fetchData("profile")}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  dataType === "profile"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }`}
                disabled={isLoading}
              >
                Company Profile
              </button>
              <button
                onClick={() => fetchData("invoices")}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  dataType === "invoices"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }`}
                disabled={isLoading}
              >
                Invoices
              </button>
              <button
                onClick={() => fetchData("contacts")}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  dataType === "contacts"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }`}
                disabled={isLoading}
              >
                Contacts
              </button>
              <button
                onClick={() => fetchData("voucherlist")}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  dataType === "voucherlist"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }`}
                disabled={isLoading}
              >
                Voucher List
              </button>
            </div>
          </div>
          {/* If we have a selected invoiceDetail, show detail; else show the main table */}
          {invoiceDetail ? (
            <InvoiceDetail
              invoice={invoiceDetail}
              onClose={() => setInvoiceDetail(null)}
            />
          ) : (
            dataType && (
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">
                    {dataType.charAt(0).toUpperCase() + dataType.slice(1)} Data
                  </h2>
                  <div className="flex space-x-2">
                    {data.length > 0 && (
                      <>
                        <button
                          onClick={exportData}
                          className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                          Export JSON
                        </button>
                        <button
                          onClick={importData}
                          className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                          Import Data
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  {isLoading ? (
                    <div className="py-8 text-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
                      <p className="mt-2 text-gray-500">
                        Loading data from Lexoffice...
                      </p>
                    </div>
                  ) : (
                    renderDataTable()
                  )}
                </div>
              </div>
            )
          )}{" "}
        </>
      )}

      {/* Toast Container configured to show on the right side */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default Lexware;
